import { DragEndEvent, DragStartEvent, useDndMonitor } from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { DragObject } from '@lmt/unicorn-shared/app/components/calendar/devextreme/common';
import { UnicornDragOverlay } from '@lmt/unicorn-shared/app/components/calendar/drag-and-drop/UnicornDragOverlay';
import { useIsSuperUser } from '@lmt/unicorn-shared/app/components/users/superuser/utils/useIsSuperUser';
import {
  getCurrentlyOpenedTabId,
  getTabId,
  removeTab,
  useTabs,
} from '@lmt/unicorn-shared/app/features/navigation/operations';
import {
  NavigationActions,
  findTab,
} from '@lmt/unicorn-shared/app/features/navigation/reducer';
import {
  getDefaultTabs,
  getTabs,
} from '@lmt/unicorn-shared/app/features/navigation/selectors';
import {
  getState,
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import React, { FC, useEffect, useMemo, useState, useRef } from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import { AddTabButton } from './components/AddTabButton';
import { Divider } from './components/Divider';
import { getPathWithoutTabPrefix, NavTab } from './components/NavTab';
import { NavTabsMenuButton } from './components/NavTabsMenuButton';
import { NavLink, updateTodayInCalendarUrl } from './types';

import styles from './NavPanel.module.scss';
import { useScopedTranslation } from '../../helpers/localization/useScopedTranslation';
import { useTabId } from '../keep-alive/TabContext';
import { useEventCallback } from '@mui/material';
import { useModal } from '../common/ui-kit/modal/useModal';
import { useIsAuthorized } from '../../features/auth/operations';
import { pages } from '../../constants/sharedRoutes';
import { formatISO } from 'date-fns';

export const NavPanel: FC = () => {
  const dispatch = useUniDispatch();
  const modals = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const i18next = useScopedTranslation('tabs.menu');
  const tabs = useTabs();
  const openedTabs: NavLink[] = useUniSelector(getTabs);
  const defaultTabs: NavLink[] = useUniSelector(getDefaultTabs);

  const isSuperUser = useIsSuperUser();
  const [draggingTabData, setDraggingTabData] = useState<
    DragObject | undefined
  >();
  const isAuth = useIsAuthorized();
  const isAuthRef = useRef(isAuth);
  isAuthRef.current = isAuth;
  const tabId = useTabId();
  const lastTabId = useRef(tabId);
  useEffect(
    function executeHandlersWhenTabChanges() {
      const oldTab = findTab(getState().navigation, lastTabId.current!);
      const newTab = findTab(getState().navigation, tabId!);
      lastTabId.current = tabId;
      oldTab?.deactivateHandler?.();
      newTab?.activateHandler?.();

      if (isAuthRef.current && tabId !== undefined)
        dispatch(
          NavigationActions.tabActivated({
            id: tabId!,
          }),
        );
    },
    [tabId],
  );
  useEffect(
    function HandleUrlChangedWithinTab() {
      // There are 2 situations we can get here:
      // 1. We pressed Back in a Browser
      //    If we did changeTabPath inside tab and then pressed Back in browser
      //    we need to make sure title and path of the tab is updated.
      //    Otherwise tab becomes "unfocused" (gray) in UI, while it should be Active (blue)
      // 2. We changed the URL via use-query-params hook.
      //    In this case we need to update current tab URL in Redux.

      const tabId = getTabId(location);
      if (tabId && tabId.startsWith('_')) {
        const tab = openedTabs.find((x) => x.id === tabId);
        const currentPath = location.pathname + location.search;
        if (tab && !currentPath.endsWith(tab.path)) {
          dispatch(
            NavigationActions.queryStringChanged({
              id: tabId,
              path: location.pathname,
              search: location.search?.length
                ? location.search.substring(1)
                : undefined,
            }),
          );
        }
      } else {
        const defaultTab = defaultTabs.find(
          (x) => x.path === location.pathname,
        );
        if (defaultTab) {
          dispatch(
            NavigationActions.defaultTabQueryStringChanged({
              title: defaultTab.title,
              search: location.search?.length
                ? location.search.substring(1)
                : undefined,
            }),
          );
        }
      }
    },
    [location],
  );

  // We do it in useMemo (not useEffect) to run it BEFORE page components render for the first time
  // That way all changes (e.g. made in postProcessCalendarUrl) are available to the components from the beginning
  useMemo(function createTabFromPastedBrowserUrl() {
    // This code handles a situation, when someone sends me a link like 'http://localhost:3006/tabs/_eiyd9sy84/patients/45'
    // In this case we should open a new tab for that patient.
    const tabIdOfCurrentUrl = getTabId(location);
    const currentTab = [...openedTabs, ...defaultTabs].find(
      (tab) => tab.id === tabIdOfCurrentUrl,
    );

    // if someone sent us a calendar link and we open it, we must set `today` search parameter to today to prevent day switching
    setTodayInCalendarUrl(currentTab, location);

    if (tabIdOfCurrentUrl && tabIdOfCurrentUrl.startsWith('_') && !currentTab) {
      const path = getPathWithoutTabPrefix(location.pathname);
      tabs.openTab(path, location.search);
    }
  }, []);

  const isCurrentTab = (path: string) => {
    // Root URL is Dashboard
    if (path.startsWith('/dashboard') && location.pathname === '/') return true;

    return (location.pathname + location.search).startsWith(path);
  };

  const closeTab = useEventCallback(async (id: string) => {
    const isCurrent = getCurrentlyOpenedTabId() === id;
    const tabs = getState().navigation.tabs;
    const hasChanges = tabs.find((tab) => tab.id === id)?.hasUnsavedChanges;
    if (hasChanges) {
      const isConfirmed = await modals.showConfirm({
        title: i18next.t('unsaved_changes_confirmation.title'),
        text: i18next.t('unsaved_changes_confirmation.text'),
        okButtonText: i18next.t('unsaved_changes_confirmation.ok_text'),
        cancelButtonText: i18next.t('unsaved_changes_confirmation.cancel_text'),
      });
      if (isConfirmed) {
        dispatch(
          NavigationActions.tabHasUnsavedChangesChanged({
            id: id,
            hasUnsavedChanges: false,
          }),
        );
        dispatch(
          NavigationActions.setHandler({
            tabId: tabId!,
            deactivateHandler: () => undefined,
          }),
        );
        removeTab(id, isCurrent, navigate)(dispatch, getState);
      }
    } else {
      removeTab(id, isCurrent, navigate)(dispatch, getState);
    }
  });

  const onDragEnd = useEventCallback(async (event: DragEndEvent) => {
    setDraggingTabData(undefined);
    if (!event.over || event.over.id === event.active.id) {
      return;
    }
    const overData = event.over.data.current as DragObject;
    if (overData.type === 'calendar-cell' || overData.type === 'visit') {
      return;
    }
    if (overData.type === 'patient' && overData.viewType !== 'tab') return;

    const activeIndex = openedTabs.findIndex((x) => x.id === event.active.id);
    const overIndex = openedTabs.findIndex((x) => x.id === event.over!.id);
    const newData = arrayMove(openedTabs, activeIndex, overIndex);
    dispatch(NavigationActions.tabsLoaded([...newData]));
  });

  const onDragStart = useEventCallback((event: DragStartEvent) => {
    const data = event.active.data.current as DragObject;
    setDraggingTabData(data);
  });
  useDndMonitor({ onDragEnd: onDragEnd, onDragMove: onDragStart });

  const visibleTabs = useMemo(() => {
    try {
      return openedTabs.filter((link) => link.permission);
    } catch (e) {
      console.error('Error during iterating over openedTabs', openedTabs, e);
      throw e;
    }
  }, [openedTabs]);

  if (isSuperUser) {
    return null;
  }

  return (
    <div className={styles.navpanel}>
      <div className={styles['tabs-container']}>
        <div className={styles.tabs} data-test-id="navPanel">
          {defaultTabs
            .filter((link) => link.permission)
            .map((link) => (
              <NavTab
                key={link.title}
                {...link}
                isSelected={isCurrentTab(link.path)}
                onClose={closeTab}
                isFixedTab={true}
              />
            ))}
          <Divider />
          <UnicornDragOverlay draggingTabData={draggingTabData} />
          <SortableContext
            items={visibleTabs.map((x) => x.id!)}
            strategy={horizontalListSortingStrategy}
          >
            {visibleTabs.map((link) => (
              <NavTab
                onClose={closeTab}
                key={link.id}
                isSelected={isCurrentTab(link.path)}
                isFixedTab={false}
                search={link.search}
                id={link.id}
                path={link.path}
              />
            ))}
          </SortableContext>
        </div>
        <AddTabButton />
      </div>
      <div>
        <NavTabsMenuButton />
      </div>
    </div>
  );
};

function setTodayInCalendarUrl(
  currentTab: NavLink | undefined,
  location: Location<any>,
) {
  let search = location.search;
  if (search.startsWith('?')) search = search.substring(1);

  if (
    pages.calendarTab.matchPath(location.pathname) ||
    (pages.calendar.matchPath(location.pathname) &&
      currentTab?.search != search)
  ) {
    location.search = updateTodayInCalendarUrl(location.search);
  }
}
